/**
 * Central registry of all import specs.
 *
 * The round-trip test iterates this list, opens the organizer workbook,
 * and asserts each spec's headers + example row line up with the .xlsx.
 * Adding a new sheet means adding a spec here — test coverage and dialog
 * compatibility come for free.
 */
import { attendeeImportSpec } from "./attendeeImportSpec";
import { eventImportSpec } from "./eventImportSpec";
import { groupImportSpec } from "./groupImportSpec";
import { locationImportSpec } from "./locationImportSpec";
import { notificationImportSpec } from "./notificationImportSpec";
import { sessionImportSpec } from "./sessionImportSpec";
import { sessionTypeImportSpec } from "./sessionTypeImportSpec";
import { surveyImportSpec } from "./surveyImportSpec";
import {
  flightImportSpec,
  lodgingImportSpec,
  transportImportSpec,
  otherTravelImportSpec,
} from "./travelImportSpecs";
import { userImportSpec } from "./userImportSpec";
import type { ImportSpec } from "./importSpec";

/**
 * Use a wide type for the registry. Each entry's TRaw / TParsed is opaque
 * to the iterator — the round-trip test only needs to read headers and
 * call `validate` against the example row, which the wide type permits.
 */
type AnyImportSpec = ImportSpec<
  Record<string, string | undefined>,
  unknown
>;

/**
 * Every import-ready sheet in the workbook. Order reflects the
 * Phase 0 / Phase 1 / Phase 2 organizer workflow:
 *   - Phase 0 (global): Users
 *   - Phase 1 (per-event setup): Event metadata, Roster, Locations
 *   - Phase 2 (per-event content): Groups, Sessions, Flights, Lodging,
 *     Transport, Other Travel
 *   - Phase 3 (per-event ops): Notifications, Surveys
 *
 * The canonical Planning Workbook generator iterates this array in order,
 * so changing the order here changes the sheet order in the .xlsx.
 */
export const IMPORT_SPECS: ReadonlyArray<AnyImportSpec> = [
  userImportSpec as AnyImportSpec,
  eventImportSpec as AnyImportSpec,
  attendeeImportSpec as AnyImportSpec,
  locationImportSpec as AnyImportSpec,
  groupImportSpec as AnyImportSpec,
  sessionTypeImportSpec as AnyImportSpec,
  sessionImportSpec as AnyImportSpec,
  flightImportSpec as AnyImportSpec,
  lodgingImportSpec as AnyImportSpec,
  transportImportSpec as AnyImportSpec,
  otherTravelImportSpec as AnyImportSpec,
  notificationImportSpec as AnyImportSpec,
  surveyImportSpec as AnyImportSpec,
];

/** Look up a spec by sheet name. Returns undefined for unknown sheets. */
export function findImportSpec(sheetName: string): AnyImportSpec | undefined {
  return IMPORT_SPECS.find((spec) => spec.sheetName === sheetName);
}
